import { MetricEntry, Timestamp } from '@griply/types';
import { calculateMetricEntryValue } from '@griply/utils';
import { isAfter, isBefore, isSameDay } from 'date-fns';
import { useMemo } from 'react';

export type UseTimeframeDifferenceOptions = {
  entries: MetricEntry[];
  startDate: Timestamp;
  endDate: Timestamp;
  startValue?: number;
  target?: number;
};

export const useTimeframeDifference = ({
  entries,
  startDate,
  endDate,
  startValue = 0,
  target,
}: UseTimeframeDifferenceOptions) =>
  useMemo(() => {
    if (!target) {
      return 0;
    }

    const sortedEntries = [...entries].sort(
      ({ date: dateA }, { date: dateB }) => dateA.getTime() - dateB.getTime(),
    );
    const isInPeriod = (entry: MetricEntry) =>
      isAfter(entry.date, startDate) && isBefore(entry.date, endDate);
    // "Entering the period" = logged on or before the period's first day. An
    // entry dated on the start day is the goal's starting value for the period
    // (its own value is not this-period progress); with none, the goal entered
    // at its startValue (e.g. "started Jul 1 at 0, first logged Jul 2").
    const enteredPeriodAt = (entry: MetricEntry) =>
      isSameDay(entry.date, startDate) || isBefore(entry.date, startDate);

    const baselineEntry = sortedEntries.reduce<MetricEntry | undefined>(
      (acc, entry) => (enteredPeriodAt(entry) ? entry : acc),
      undefined,
    );
    const lastEntryInPeriod = sortedEntries.reduce<MetricEntry | undefined>(
      (acc, entry) => (isInPeriod(entry) ? entry : acc),
      undefined,
    );

    // Nothing logged within the period, or only the start-day baseline itself
    // (baseline === current) → no measurable delta.
    if (!lastEntryInPeriod || baselineEntry === lastEntryInPeriod) {
      return 0;
    }

    const baselineValue = baselineEntry
      ? calculateMetricEntryValue(baselineEntry.id, entries, startValue)
      : startValue;

    const range = target - startValue;
    const currentPercentage =
      ((calculateMetricEntryValue(lastEntryInPeriod.id, entries, startValue) -
        startValue) /
        range) *
      100;
    const startPercentage = ((baselineValue - startValue) / range) * 100;

    const invert = !!startValue && startValue > target;

    return (currentPercentage - startPercentage) * (invert ? -1 : 1) || 0;
  }, [target, entries, startValue, startDate, endDate]);
